import { useMutation } from '@tanstack/react-query';
import * as React from 'react';

import { apiService } from '../../../services/api';
import { trackReportEvent } from '../lib/analytics';
import { getReportDownload } from '../lib/downloadReport';
import { getReportCopy } from '../lib/reportCopy';
import {
  findLatestJobForRestaurant,
  selectRemoveReportJob,
  selectReportJobs,
  selectUpsertReportJob,
  useReportJobStore,
} from '../store/reportJob.store';
import type {
  GenerateReportStatus,
  ReportGenerationJobResponse,
  ReportGenerationRequestPayload,
} from '../types';

export interface UseGenerateReportOptions {
  restaurantId: number | string | null | undefined;
  locale?: string | null;
  onGenerated?: (result: {
    jobId: string;
    downloadUrl: string;
    expiresAt: string | null;
    reportType: string | null;
    startedAt: string | null;
  }) => void;
  onFailed?: (
    error: Error,
    context: { jobId: string | null; reportType: string | null },
  ) => void;
}

export interface TriggerReportArgs {
  payload: ReportGenerationRequestPayload;
  idempotencyKey?: string;
  reportType?: string;
}

export interface UseGenerateReportResult {
  status: GenerateReportStatus;
  jobId: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  reportType: string | null;
  startedAt: string | null;
  isLoading: boolean;
  error: Error | null;
  announcement: string;
  triggerReport: (
    args: TriggerReportArgs,
  ) => Promise<ReportGenerationJobResponse>;
  cancel: () => void;
  reset: () => void;
}

export function useGenerateReport(
  options: UseGenerateReportOptions,
): UseGenerateReportResult {
  const { restaurantId, locale, onGenerated, onFailed } = options;

  const copy = React.useMemo(() => getReportCopy(locale ?? 'es-ES'), [locale]);

  const reportJobs = useReportJobStore(selectReportJobs);
  const upsertReportJob = useReportJobStore(selectUpsertReportJob);
  const removeReportJob = useReportJobStore(selectRemoveReportJob);

  const persistedJob = React.useMemo(
    () => findLatestJobForRestaurant(reportJobs, restaurantId ?? null),
    [reportJobs, restaurantId],
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionError, setSubmissionError] = React.useState<Error | null>(
    null,
  );
  const notifiedTerminalRef = React.useRef<string | null>(null);

  const reset = React.useCallback(() => {
    if (persistedJob?.jobId) {
      removeReportJob(persistedJob.jobId);
    }
    setIsSubmitting(false);
    setSubmissionError(null);
    notifiedTerminalRef.current = null;
  }, [persistedJob?.jobId, removeReportJob]);

  const cancel = reset;

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      restaurantId: targetRestaurant,
      payload,
      idempotencyKey,
      reportType,
    }: {
      restaurantId: number | string;
      payload: ReportGenerationRequestPayload;
      idempotencyKey?: string;
      reportType?: string;
    }) =>
      apiService.postMarketingReport({
        restaurantId: targetRestaurant,
        payload,
        ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
        ...(reportType !== undefined ? { reportType } : {}),
      }),
  });

  const triggerReport = React.useCallback(
    async ({
      payload,
      idempotencyKey,
      reportType,
    }: TriggerReportArgs): Promise<ReportGenerationJobResponse> => {
      if (!restaurantId) {
        const missingRestaurantError = new Error(copy.errors.missingRestaurant);
        setSubmissionError(missingRestaurantError);
        throw missingRestaurantError;
      }

      setIsSubmitting(true);
      setSubmissionError(null);
      notifiedTerminalRef.current = null;
      const startTimestamp = new Date().toISOString();

      try {
        const response = await mutateAsync({
          restaurantId,
          payload,
          ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
          ...(reportType !== undefined ? { reportType } : {}),
        });

        const baseJob = {
          jobId: response.jobId,
          restaurantId,
          status: response.status,
          reportType: response.reportType ?? null,
          downloadUrl: null,
          expiresAt: null,
          locale: locale ?? 'es-ES',
          startedAt: startTimestamp,
          error: null,
          timeRange: payload.timeRange,
          ...(payload.templateId !== undefined
            ? { templateId: payload.templateId }
            : {}),
        };

        trackReportEvent('report.generate', {
          jobId: response.jobId,
          restaurantId: restaurantId ?? null,
          reportType: response.reportType,
          startedAt: startTimestamp,
          periodType: payload.timeRange.type,
          periodStart: payload.timeRange.start ?? null,
          periodEnd: payload.timeRange.end ?? null,
          templateId: payload.templateId ?? null,
          locale: payload.locale ?? locale ?? 'es-ES',
        });

        if (response.status === 'ready') {
          try {
            const download = await getReportDownload(response.jobId);
            upsertReportJob({
              ...baseJob,
              status: 'ready',
              downloadUrl: download.downloadUrl,
              expiresAt: download.expiresAt ?? null,
            });
          } catch (deliveryError) {
            const failure =
              deliveryError instanceof Error
                ? deliveryError
                : new Error(copy.errors.download);
            upsertReportJob({
              ...baseJob,
              status: 'failed',
              error: failure.message,
            });
          }
        } else if (response.status === 'failed') {
          upsertReportJob({
            ...baseJob,
            status: 'failed',
            error: copy.errors.start,
          });
        } else {
          upsertReportJob(baseJob);
        }

        return response;
      } catch (generateError) {
        const err =
          generateError instanceof Error
            ? generateError
            : new Error(copy.errors.start);
        setSubmissionError(err);
        trackReportEvent('report.generate.failure', {
          jobId: null,
          restaurantId: restaurantId ?? null,
          reportType: null,
          error: err.message,
          periodType: payload.timeRange.type,
          periodStart: payload.timeRange.start ?? null,
          periodEnd: payload.timeRange.end ?? null,
          templateId: payload.templateId ?? null,
          latencyMs: Date.now() - Date.parse(startTimestamp),
        });
        onFailed?.(err, { jobId: null, reportType: null });
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [copy, locale, mutateAsync, onFailed, restaurantId, upsertReportJob],
  );

  React.useEffect(() => {
    if (
      !persistedJob ||
      (persistedJob.status !== 'ready' && persistedJob.status !== 'failed')
    ) {
      return;
    }

    const terminalKey = `${persistedJob.jobId}:${persistedJob.status}`;
    if (notifiedTerminalRef.current === terminalKey) {
      return;
    }
    notifiedTerminalRef.current = terminalKey;

    const parsedStartedAt = persistedJob.startedAt
      ? Date.parse(persistedJob.startedAt)
      : NaN;
    const latencyMs = Number.isNaN(parsedStartedAt)
      ? null
      : Math.max(0, Date.now() - parsedStartedAt);
    const analyticsContext = {
      jobId: persistedJob.jobId,
      restaurantId: persistedJob.restaurantId,
      reportType: persistedJob.reportType,
      periodType: persistedJob.timeRange?.type ?? null,
      periodStart: persistedJob.timeRange?.start ?? null,
      periodEnd: persistedJob.timeRange?.end ?? null,
      templateId: persistedJob.templateId ?? null,
      latencyMs,
    };

    if (persistedJob.status === 'ready' && persistedJob.downloadUrl) {
      trackReportEvent('report.generate.success', analyticsContext);
      onGenerated?.({
        jobId: persistedJob.jobId,
        downloadUrl: persistedJob.downloadUrl,
        expiresAt: persistedJob.expiresAt,
        reportType: persistedJob.reportType,
        startedAt: persistedJob.startedAt,
      });
      return;
    }

    const failure = new Error(persistedJob.error ?? copy.errors.start);
    trackReportEvent('report.generate.failure', {
      ...analyticsContext,
      error: failure.message,
    });
    onFailed?.(failure, {
      jobId: persistedJob.jobId,
      reportType: persistedJob.reportType,
    });
  }, [copy.errors.start, onFailed, onGenerated, persistedJob]);

  const status: GenerateReportStatus =
    isSubmitting || isPending ? 'submitting' : (persistedJob?.status ?? 'idle');
  const isLoading =
    status === 'submitting' || status === 'pending' || status === 'processing';
  const error = React.useMemo(() => {
    if (submissionError) return submissionError;
    return persistedJob?.error ? new Error(persistedJob.error) : null;
  }, [persistedJob?.error, submissionError]);
  const announcement =
    status === 'submitting'
      ? copy.announcements.starting
      : status === 'pending' || status === 'processing'
        ? copy.announcements.inProgress
        : status === 'ready'
          ? copy.announcements.ready
          : status === 'failed'
            ? copy.announcements.failed
            : '';

  return React.useMemo(
    () => ({
      status,
      jobId: persistedJob?.jobId ?? null,
      downloadUrl: persistedJob?.downloadUrl ?? null,
      expiresAt: persistedJob?.expiresAt ?? null,
      reportType: persistedJob?.reportType ?? null,
      startedAt: persistedJob?.startedAt ?? null,
      isLoading,
      error,
      announcement,
      triggerReport,
      cancel,
      reset,
    }),
    [
      announcement,
      cancel,
      error,
      isLoading,
      persistedJob,
      reset,
      status,
      triggerReport,
    ],
  );
}
